import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { DeletionMode } from '../api/deleteComponent';

interface DeleteComponentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  componentName: string;
  willAwaitDeinstrumentPr: boolean;
  isDeleting: boolean;
  onConfirm: (mode: DeletionMode) => void;
}

function DeleteComponentDescription({
  name,
  willAwaitDeinstrumentPr,
}: {
  name: string;
  willAwaitDeinstrumentPr: boolean;
}) {
  if (willAwaitDeinstrumentPr) {
    return (
      <>
        Choose how to remove <strong>{name}</strong>. QTrace instrumented this
        component, so a de-instrumentation PR will be raised first. The card
        stays visible until that PR is merged.
      </>
    );
  }

  return (
    <>
      Choose how to remove <strong>{name}</strong>. This component was not
      instrumented by QTrace, so no de-instrumentation PR will be raised.
    </>
  );
}

const DeleteComponentDialog = ({
  open,
  onOpenChange,
  componentName,
  willAwaitDeinstrumentPr,
  isDeleting,
  onConfirm,
}: DeleteComponentDialogProps) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Delete Component</DialogTitle>
        <DialogDescription asChild>
          <div className="space-y-3 text-sm text-muted-foreground">
            <DeleteComponentDescription
              name={componentName}
              willAwaitDeinstrumentPr={willAwaitDeinstrumentPr}
            />
            <div className="space-y-2 rounded-md border border-solid border-[var(--inv-border)] bg-[var(--inv-surface-muted)] p-3 text-[var(--inv-text-body)]">
              <p className="m-0 font-medium text-[var(--inv-text)]">
                De-instrument
              </p>
              <p className="m-0 text-[0.8125rem] leading-relaxed">
                Archives component data in QTrace after de-instrumentation. You
                can restore the card later.
              </p>
            </div>
            <div className="space-y-2 rounded-md border border-solid border-[var(--inv-border)] bg-[var(--inv-surface-muted)] p-3 text-[var(--inv-text-body)]">
              <p className="m-0 font-medium text-[var(--inv-text)]">
                Permanently delete
              </p>
              <p className="m-0 text-[0.8125rem] leading-relaxed">
                Removes all component data from the database after
                de-instrumentation. This cannot be undone.
              </p>
            </div>
          </div>
        </DialogDescription>
      </DialogHeader>
      <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button
          variant="outline"
          onClick={() => onOpenChange(false)}
          disabled={isDeleting}>
          Cancel
        </Button>
        <Button
          variant="outline"
          onClick={() => onConfirm('soft')}
          disabled={isDeleting}>
          {isDeleting ? 'Working...' : 'De-instrument'}
        </Button>
        <Button
          variant="destructive"
          onClick={() => onConfirm('hard')}
          disabled={isDeleting}>
          {isDeleting ? 'Working...' : 'Permanently delete'}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export default DeleteComponentDialog;
